import React from 'react'
import PropTypes from 'prop-types'
import { isNil, isEmpty } from 'ramda'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { QUERY_BOX_TYPES, CAN_INPUT, PIN } from 'utils/constants'
import strings from 'utils/strings'
import { isCanOutput } from 'utils/types/features'

import {
  getShowOutputEvaluation,
  getHasNativeOutput,
  getOutputPinSwitches,
} from 'selectors/feature/outputs'
import { getIsSimulating } from 'selectors/navigator'
import { makeOutputQueryLogicNode } from 'selectors/feature/logicNodes'
import { DrawerDivider, Header } from 'components/patterns'
import ConditionSimulator from 'components/feature/Simulation/ConditionSimulator'
import LogicSetEvaluationBanner from 'components/feature/Simulation/LogicSetEvaluationBanner'
import BitGrid from 'components/feature/NavigatorPanes/editors/base/BitGrid'
import { ReadOnlyParameterValue } from 'components/feature/NavigatorPanes/editors/base'
import { OutputConnectorFinder } from 'components/feature/ConnectorFinderMenu'
import ReadOnlyScaling from 'components/feature/NavigatorPanes/editors/base/ReadOnlyScaling'
import {
  makeLogicSetSimulationsSelector,
  getIsActiveSimulatedPowerNetLevel,
  getIsSimulatedVirtualFuseOpen,
} from 'selectors/feature/simulation'
import Evaluation from 'components/feature/NavigatorPanes/editors/base/Evaluation'
import OutputTypeDescriptionLabel from 'components/feature/OutputNode/OutputTypeDescriptionLabel'
import InputSwitchList from 'components/feature/NavigatorPanes/editors/base/InputSwitchList'
import Circuit from 'components/feature/Harnesses/Circuit'
import { makeGetPinCircuit } from 'selectors/feature/harnesses'
import { getOutputValues } from 'components/feature/OutputNode/utils'
import HoverableInfoIcon from 'components/patterns/HoverableInfoIcon'
import { PowerNetLevelValue } from './OutputNodeReadonly.style'
import OutputQuery from '../OutputQuery'
import CanOutputSection from '../OutputNodeEditor/CanOutputSection'
import PowerNetLevelTable from '../OutputNodeEditor/PowerNetLevelEditor/PowerNetLevelTableSVG'

const OutputNodeReadonly = ({
  parentId: fnId,
  id,
  output,
  featureModuleType,
  featureType,
}) => {
  // Use default empty object if output is null to prevent crashes during navigation
  const {
    mappedOutputId,
    outputDescription,
    description,
    connector,
    location,
    numberOfBits,
    startBit,
    value,
    type,
    scalingQuery,
    powerNetLevel,
    interfacePins,
  } = output || {}

  const circuit = useMakeSelector(makeGetPinCircuit, { id: mappedOutputId })
  const { outputting } = useMakeSelector(makeLogicSetSimulationsSelector, {
    queryUri: id,
  })

  const isActivePowerNetLevel = useSelector(getIsActiveSimulatedPowerNetLevel, {
    powerNetLevel,
  })

  const virtualFuseOpen = useSelector(getIsSimulatedVirtualFuseOpen, {
    outputId: id,
  })

  const outputPinSwitches = useSelector(getOutputPinSwitches, {
    mappedOutputId,
  })

  const showOutputEvaluation = useSelector(getShowOutputEvaluation, {
    fnId,
    id,
  })
  const isSimulating = useSelector(getIsSimulating)
  const hasNativeOutput = useSelector(getHasNativeOutput, { id: fnId })
  const logicNode = useMakeSelector(makeOutputQueryLogicNode, { fnId })

  const {
    subType,
    queryType,
    numberOfBits: inputNumOfBits,
    startBit: inputStartBit,
    switchNumber,
    switchDescription,
    uri,
  } = logicNode || {}
  const hasSwitches = !isEmpty(outputPinSwitches)
  const isCanInput = subType === CAN_INPUT

  const showInputSwitchNumber = switchNumber && hasNativeOutput

  // Return null if output is not available (e.g., during navigation/unmounting)
  // MUST check BEFORE calling getOutputValues to prevent crashes
  if (!output) {
    return null
  }

  const outputValues = getOutputValues({
    ...output,
    featureModuleType,
    featureType,
    circuit,
  })

  const showCircuit = type === PIN && !isCanOutput(type)

  const showOutputControlSimulationButtons = isSimulating && hasNativeOutput
  const showConnectorFinder = mappedOutputId && type === PIN

  return (
    <>
      <Header type="h3">{output.description}</Header>
      {isSimulating && (
        <LogicSetEvaluationBanner
          evaluation={
            outputting && isActivePowerNetLevel && virtualFuseOpen === false
          }
        />
      )}
      <DrawerDivider />
      {showOutputControlSimulationButtons ? (
        <>
          <ConditionSimulator uri={uri} />
          <OutputTypeDescriptionLabel
            id={id}
            fnId={fnId}
            mappedOutputId={mappedOutputId}
            type={type}
            outputDescription={outputDescription}
            description={description}
            connector={connector}
            location={location}
            numberOfBits={numberOfBits}
          />
        </>
      ) : (
        <OutputTypeDescriptionLabel
          id={id}
          fnId={fnId}
          mappedOutputId={mappedOutputId}
          type={type}
          outputDescription={outputDescription}
          description={description}
          connector={connector}
          location={location}
          numberOfBits={numberOfBits}
        />
      )}
      {showConnectorFinder && (
        <OutputConnectorFinder output={output} interfacePins={interfacePins} />
      )}
      {outputValues.map(
        ({ label, value: val }) =>
          !isNil(val) && (
            <ReadOnlyParameterValue
              key={label}
              label={label}
              value={
                label === strings.power_net_level ? (
                  <PowerNetLevelValue>
                    {val}
                    <HoverableInfoIcon
                      overlayClassName="autoWidth svg-tooltip"
                      tooltip={<PowerNetLevelTable />}
                      tooltipPosition="topRight"
                    />
                  </PowerNetLevelValue>
                ) : (
                  val
                )
              }
            />
          )
      )}
      {isCanOutput(type) && (
        <>
          <CanOutputSection
            mappedOutputId={mappedOutputId}
            numberOfBits={numberOfBits}
            showCanId={false}
            className="output-container"
          />
          <BitGrid type="output" groupSize={numberOfBits} value={startBit} />
        </>
      )}
      {hasSwitches && (
        <InputSwitchList
          currentInputOrOutputSwitches={outputPinSwitches}
          marginTop={'0px'}
        />
      )}
      {!showOutputControlSimulationButtons && <DrawerDivider />}
      {showCircuit && <Circuit id={mappedOutputId} outputId={id} />}
      {scalingQuery && (
        <ReadOnlyScaling uri={scalingQuery} isScalingQuery={true} />
      )}
      {
        // TODO: there is dupe bit/byte block labels
        hasNativeOutput && isCanInput && (
          <BitGrid
            type="input"
            groupSize={inputNumOfBits}
            value={inputStartBit}
          />
        )
      }
      {(!hasNativeOutput ||
        (hasNativeOutput && QUERY_BOX_TYPES.has(queryType))) && (
        <OutputQuery fnId={fnId} id={id} />
      )}
      {showOutputEvaluation && <Evaluation value={value} readonly />}
      {showInputSwitchNumber && (
        <>
          <ReadOnlyParameterValue
            value={switchNumber}
            label={strings.partnumber_title}
          />
          <ReadOnlyParameterValue
            value={switchDescription}
            label={strings.desc_title}
          />
        </>
      )}
    </>
  )
}

OutputNodeReadonly.propTypes = {
  featureModuleType: PropTypes.string.isRequired,
  switchNumber: PropTypes.string,
  switchDescription: PropTypes.string,
  output: PropTypes.shape({
    current: PropTypes.number,
    polarity: PropTypes.string,
    type: PropTypes.string,
    moduleType: PropTypes.string,
    activeStateRange: PropTypes.array,
    description: PropTypes.string,
    location: PropTypes.string,
    connector: PropTypes.string,
    mappedOutputId: PropTypes.string,
    outputDescription: PropTypes.string,
    id: PropTypes.string,
    fnId: PropTypes.string,
    operator: PropTypes.number,
    value: PropTypes.number,
    switchNumber: PropTypes.string,
    switchDescription: PropTypes.string,
    scalingQuery: PropTypes.string,
    startBit: PropTypes.number,
    numberOfBits: PropTypes.number,
    floatingCircuit: PropTypes.string,
    powerNetLevel: PropTypes.number,
    interfacePins: PropTypes.array,
  }),
  featureType: PropTypes.string,
  parentId: PropTypes.string,
  id: PropTypes.string,
}

export default OutputNodeReadonly
